import { reactive } from 'vue'
import axios from 'axios'
import { mergeStep } from '@/utils'

interface DomainStatus {
  url: string
  faulty: boolean
}

export function useDomain({ remote }: { remote: boolean }) {
  const state = reactive({
    domains: [] as DomainStatus[],
    isInitialized: false,
  })

  async function initialize() {
    if (state.isInitialized) return
    if (remote) {
      try {
        const response = await axios.get(import.meta.env.VITE_BASE_URL)
        const newDomainUrls = response.data.data.filter(
          (url: string) => url.replace(/\/$/, '').trim() !== '',
        )
        if (Array.isArray(newDomainUrls) && newDomainUrls.length > 0) {
          const existingUrls = state.domains.map((d) => d.url)
          if (JSON.stringify(existingUrls) !== JSON.stringify(newDomainUrls)) {
            console.log('API domains have changed, resetting.')
            state.domains = newDomainUrls.map((url) => ({ url, faulty: false }))
          }
        } else {
          throw new Error('Invalid domains response')
        }
      } catch (error) {
        console.error('Failed to fetch API domains, using cache.', error)
        if (!state.domains.length) {
          const fallbackUrl = import.meta.env.VITE_SERVER_BASEURL
          state.domains = [{ url: fallbackUrl, faulty: false }]
          console.log('Using fallback API domain:', fallbackUrl)
        }
      }
    } else {
      state.domains = [{ url: import.meta.env.VITE_SERVER_BASEURL, faulty: false }]
    }
    state.isInitialized = true
  }

  function markCurrentAsFaulty(url: string) {
    const current = state.domains.find((d) => d.url === url)
    if (current) {
      current.faulty = true
      return true
    } else {
      return false
    }
  }

  function areAllDomainsFaulty(): boolean {
    if (!state.domains.length) return true
    return state.domains.every((d) => d.faulty)
  }

  const getCurrentUrl = () => {
    return state.domains.find((d) => !d.faulty)?.url
  }

  return {
    initialize: mergeStep(initialize),
    getCurrentUrl,
    markCurrentAsFaulty,
    areAllDomainsFaulty,
    isInitialized: () => state.isInitialized,
  }
}
